// API configuration with validation and dynamic URL detection
let BACKEND_URL;
try {
  // Check multiple sources for the backend URL (process.env for CRA)
  BACKEND_URL = 
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) ||
    '';
} catch (e) {
  BACKEND_URL = '';
}

// Determine API base URL
let API;
if (typeof window !== 'undefined') {
  const currentDomain = window.location.hostname;
  
  // PRIORITY 1: Preview/Emergent domains - always use same origin
  if (currentDomain.includes('preview.emergentagent.com') || currentDomain.includes('emergent.host')) {
    API = window.location.origin;
  }
  // PRIORITY 2: Production iwashere.me domain
  else if (currentDomain === 'iwashere.me' || currentDomain === 'www.iwashere.me') {
    API = 'https://iwashere.me';
  }
  // PRIORITY 2b: Club domains - route to iwashere.me API
  else if (currentDomain === 'travelrock.club' || currentDomain === 'www.travelrock.club') {
    API = 'https://iwashere.me';
  }
  else if (currentDomain === 'curithekitten.com' || currentDomain === 'www.curithekitten.com') {
    API = 'https://iwashere.me';
  }
  // PRIORITY 3: Local development
  else if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    // For local dev, use env variable or fallback to local backend
    const envBackendUrl = BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
    if (envBackendUrl && envBackendUrl.length > 0) {
      API = envBackendUrl;
    } else {
      API = 'http://localhost:8001';
    }
  }
  // PRIORITY 4: Other domains - use environment or origin
  else {
    const envBackendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env?.REACT_APP_BACKEND_URL;
    if (envBackendUrl) {
      API = envBackendUrl;
    } else if (currentDomain === 'trackme.info' || currentDomain === 'www.trackme.info') {
      API = 'https://trackme.info';
    } else {
      API = window.location.origin;
    }
  }
} else {
  // Server-side or build time
  API = process.env.REACT_APP_BACKEND_URL || import.meta.env?.REACT_APP_BACKEND_URL || 'http://localhost:8001';
}

// Exported so components can use the same dynamic-detected base URL instead
// of process.env.REACT_APP_BACKEND_URL (which gets baked in at build time and
// can't survive a production-slot move / domain change).
export const API_BASE = API;

class TrackMeAPI {
  // Simple in-memory cache with TTL
  _cache = new Map();
  _cacheTTL = {
    stats: 60000,           // 1 minute for basic stats
    engagement: 120000,     // 2 minutes for engagement stats
    items: 30000            // 30 seconds for items list
  };

  _getCached(key) {
    const cached = this._cache.get(key);
    if (cached && Date.now() < cached.expiry) {
      console.log(`📦 Cache hit: ${key}`);
      return cached.data;
    }
    return null;
  }

  _setCache(key, data, ttlKey = 'stats') {
    const ttl = this._cacheTTL[ttlKey] || 60000;
    this._cache.set(key, {
      data,
      expiry: Date.now() + ttl
    });
  }

  clearCache(key = null) {
    if (key) {
      this._cache.delete(key);
    } else {
      this._cache.clear();
    }
  }

  getUserEmail() {
    try {
      const storedUser = localStorage.getItem('trackme_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.email || null;
      }
      return null;
    } catch (error) {
      console.warn('Error getting user email:', error);
      return null;
    }
  }

  getUserRole() {
    try {
      const storedUser = localStorage.getItem('trackme_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.role || null;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  async get(endpoint) {
    try {
      // Add aggressive cache busting
      const separator = endpoint.includes('?') ? '&' : '?';
      const cacheBuster = `${separator}_t=${Date.now()}&_r=${Math.random()}`;
      const fullUrl = `${API}/api${endpoint}${cacheBuster}`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.detail || errorMessage;
          }
        } catch (e) {
          // Parse failed, use the default error message above
          console.warn('Could not parse error response:', e.message);
        }
        throw new Error(errorMessage);
      }
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        throw new Error('Invalid response format - expected JSON');
      }
    } catch (error) {
      console.error(`API GET ${endpoint} failed:`, error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await fetch(`${API}/api${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.detail || errorMessage;
          }
        } catch (e) {
          console.warn('Could not parse error response:', e.message);
        }
        throw new Error(errorMessage);
      }
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        throw new Error('Invalid response format - expected JSON');
      }
    } catch (error) {
      console.error(`API POST ${endpoint} failed:`, error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await fetch(`${API}/api${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
        } catch (e) {}
        throw new Error(errorMessage);
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }
      return {};
    } catch (error) {
      console.error(`API PUT ${endpoint} failed:`, error);
      throw error;
    }
  }


  async postFormData(endpoint, formData) {
    const cacheBuster = `_cb=${Date.now()}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const apiEndpoint = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
    const fullUrl = `${API}${apiEndpoint}${separator}${cacheBuster}`;
    
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for bulk operations
        
        const response = await fetch(fullUrl, {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          let errorDetail = response.statusText;
          try {
            const errorData = await response.json();
            if (errorData && errorData.detail) {
              errorDetail = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
            } else if (errorData && errorData.message) {
              errorDetail = errorData.message;
            }
          } catch (e) { console.warn('Could not parse error response:', e.message); }
          
          // Retry on server errors (5xx)
          if (response.status >= 500 && attempt < maxRetries) {
            console.warn(`Server error ${response.status}, retrying (${attempt}/${maxRetries})...`);
            await new Promise(r => setTimeout(r, 1500 * attempt));
            continue;
          }
          throw new Error(errorDetail || `Server error: ${response.status}`);
        }
        return response.json();
      } catch (error) {
        lastError = error;
        
        // Don't retry if it's a client-side validation error
        if (error.message && !error.message.includes('Failed to fetch') && !error.message.includes('aborted') && !error.message.includes('NetworkError')) {
          throw error;
        }
        
        if (attempt < maxRetries) {
          console.warn(`Network error, retrying (${attempt}/${maxRetries})...`);
          await new Promise(r => setTimeout(r, 2000 * attempt));
        }
      }
    }
    
    // All retries exhausted
    console.error('All retry attempts failed:', lastError);
    throw new Error('Unable to reach the server after multiple attempts. Please check your connection and try again.');
  }

  async patchFormData(endpoint, formData) {
    try {
      const cacheBuster = `_cb=${Date.now()}`;
      const separator = endpoint.includes('?') ? '&' : '?';
      const apiEndpoint = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
      const fullUrl = `${API}${apiEndpoint}${separator}${cacheBuster}`;
      
      const response = await fetch(fullUrl, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        let errorDetail = response.statusText;
        try {
          const errorData = await response.json();
          if (errorData && errorData.detail) {
            errorDetail = errorData.detail;
          }
        } catch (e) {
          console.warn('Could not parse error response body:', e);
        }
        throw new Error(errorDetail || `API Error: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error('📡 API PATCH Error:', error);
      throw error;
    }
  }

  // Items endpoints (cache-busting version)
  async getItems(searchTerm = '', featured = false) {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (featured) params.append('featured', 'true');
    
    const queryString = params.toString();
    
    // Try new bypass endpoint first, then fallback chain
    try {
      return await this.get(`/bypass-items${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      console.warn('Bypass endpoint failed, trying cache-busting v2:', error);
      try {
        return await this.get(`/items-v2${queryString ? `?${queryString}` : ''}`);
      } catch (error2) {
        console.warn('Cache-busting v2 failed, trying original:', error2);
        return await this.get(`/items${queryString ? `?${queryString}` : ''}`);
      }
    }
  }

  async getItem(uid) {
    // Pass viewer_role so the backend can skip "detach from batch" for admin viewers
    const viewerParam = this.getUserRole() === 'admin' ? '?viewer_role=admin' : '';
    // Use new bypass endpoint first, then emergency, then fallback
    try {
      console.log('🔍 getItem called - trying bypass endpoint for UID:', uid);
      const response = await this.get(`/bypass-item-details/${uid}${viewerParam}`);
      console.log('✅ Bypass endpoint success:', response);
      return response;
    } catch (error) {
      console.warn('❌ Bypass item details failed:', error);
      console.warn('❌ Bypass error message:', error.message);
      
      // Check if this is a security validation error (invalid QR code)
      // Don't fall back to other endpoints for security errors - these should be handled properly
      if (error.message && (
          error.message.includes('QR code not found') ||
          error.message.includes('not generated through iWasHere.me') ||
          error.message.includes('404')
      )) {
        console.log('🚨 Security validation error detected - not attempting fallback');
        console.log('🚨 Re-throwing security error:', error.message);
        throw error; // Re-throw the security error without fallback
      }
      
      try {
        console.log('🔍 Trying emergency endpoint...');
        const response = await this.get(`/qr-lookup/${uid}${viewerParam}`);
        console.log('✅ Emergency endpoint success:', response);
        return response;
      } catch (error2) {
        console.warn('❌ Emergency endpoint failed:', error2);
        console.warn('❌ Emergency error message:', error2.message);
        
        // Check for security errors on fallback too
        if (error2.message && (
            error2.message.includes('QR code not found') ||
            error2.message.includes('not generated through TrackMe.info') ||
            error2.message.includes('404')
        )) {
          console.log('🚨 Security validation error detected on fallback - not attempting further fallback');
          console.log('🚨 Re-throwing fallback security error:', error2.message);
          throw error2;
        }
        
        // Fallback to original endpoint if both fail (but not for security errors)
        console.warn('🔍 Emergency endpoint failed, trying final fallback...');
        console.log('🔍 Trying item-details endpoint...');
        const response = await this.get(`/item-details/${uid}${viewerParam}`);
        console.log('✅ Final fallback success:', response);
        return response;
      }
    }
  }

  async getItemDetails(uid) {
    // Alias for getItem to maintain backward compatibility
    try {
      console.log('🔍 getItemDetails called for UID:', uid);
      const response = await this.getItem(uid);
      console.log('📨 getItemDetails response:', response);
      
      // Handle the response structure for bypass endpoints
      if (response && response.item) {
        console.log('✅ Returning response.item:', response.item);
        return response.item;
      }
      
      // Handle direct item response
      console.log('✅ Returning direct response:', response);
      return response;
    } catch (error) {
      console.error('❌ Get item details failed - ERROR CAUGHT:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error type:', typeof error);
      
      // Re-throw the error so QRScanHandler can catch it
      throw error;
    }
  }

  // Get paginated stories for an item (for performance on items with many stories)
  async getItemStoriesPaginated(uid, page = 1, perPage = 10) {
    try {
      const response = await this.fetchWithAuth(`${API}/items/${uid}/stories?page=${page}&per_page=${perPage}`);
      return response;
    } catch (error) {
      console.error('Error fetching paginated stories:', error);
      throw error;
    }
  }

  async getQRCode(uid) {
    return `${API}/qr/${uid}.png`;
  }

  // Export journey as PDF - special handling for file download
  async exportJourneyPDF(itemId) {
    try {
      const response = await fetch(`${API}/api/items/${itemId}/export-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: this.getUserEmail()
        })
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('PDF export is a premium feature. Please upgrade your subscription to access this functionality.');
        }
        if (response.status === 401) {
          throw new Error('Please log in to export PDFs. This is a premium feature.');
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to export PDF');
      }

      // Return blob for PDF file download
      return await response.blob();
    } catch (error) {
      console.error('Export journey PDF error:', error);
      throw error;
    }
  }

  async generateStoryCard(itemUid, storyId) {
    // Download PDF file directly (not JSON response)
    const url = `${API}/api/admin/generate-story-card/${itemUid}/${storyId}`;
    console.log('🌐 Full API URL:', url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    console.log('📡 Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Get the blob and filename
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `story-card-${storyId.slice(0, 8)}.pdf`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Return blob and filename for download
    return { blob, filename };
  }

  async exportItemPDF(itemId) {
    return this.exportJourneyPDF(itemId);
  }

  // Alias for backward compatibility with ItemDetailPage
  async exportItemJourney(itemId) {
    return this.exportJourneyPDF(itemId);
  }

  async createItem(itemData) {
    const formData = new FormData();
    formData.append('name', itemData.name);
    formData.append('creatorName', itemData.creatorName);
    formData.append('creatorEmail', itemData.creatorEmail);
    formData.append('initialLocation', itemData.initialLocation);
    formData.append('initialStory', itemData.initialStory);
    formData.append('categories', JSON.stringify(itemData.categories || []));
    formData.append('privateTag', itemData.privateTag || '');
    formData.append('coordinates', itemData.coordinates || '');
    
    // Add initial image if provided
    if (itemData.initialImage) {
      formData.append('initialImage', itemData.initialImage);
    }
    
    return this.postFormData('/items', formData);
  }

  // Scanning endpoints
  async scanItem(uid) {
    return this.post(`/scan/${uid}`, {});
  }

  // Stories endpoints
  async addStory(uid, storyData) {
    const formData = new FormData();
    formData.append('userName', storyData.name);
    formData.append('location', storyData.location);
    formData.append('story', storyData.story);
    
    // Optional email for finders
    if (storyData.email) {
      formData.append('userEmail', storyData.email);
    }
    
    // Add notification preference
    if (storyData.wantsNotifications !== undefined) {
      formData.append('wantsNotifications', storyData.wantsNotifications.toString());
    }
    
    if (storyData.coordinates) {
      formData.append('latitude', storyData.coordinates.lat);
      formData.append('longitude', storyData.coordinates.lng);
    }
    
    if (storyData.photo) {
      formData.append('photo', storyData.photo);
    }
    
    // Add scan_mode for guestbook tracking (qr or gps)
    if (storyData.scan_mode) {
      formData.append('scan_mode', storyData.scan_mode);
    }

    // hCaptcha token — required by backend when submitter is anonymous
    // (no userEmail). Signed-in / email-verified flows skip the challenge.
    if (storyData.hcaptcha_token) {
      formData.append('hcaptcha_token', storyData.hcaptcha_token);
    }

    return this.postFormData(`/api/items/${uid}/stories`, formData);
  }

  async getRecentActivity() {
    return this.get('/stories/recent');
  }

  // Statistics (with caching)
  async getStats(privateTag = null) {
    console.log('📊 getStats called', privateTag ? `(scope=${privateTag})` : '');
    const cacheKey = privateTag ? `stats:${privateTag}` : 'stats';
    const cached = this._getCached(cacheKey);
    if (cached) {
      console.log('📊 Returning cached stats:', cached);
      return cached;
    }

    console.log('📊 Fetching fresh stats from API...');
    try {
      const path = privateTag
        ? `/stats?private_tag=${encodeURIComponent(privateTag)}`
        : '/stats';
      const data = await this.get(path);
      console.log('📊 Stats received:', data);
      this._setCache(cacheKey, data, 'stats');
      return data;
    } catch (error) {
      console.error('📊 Error fetching stats:', error);
      throw error;
    }
  }

  // Search
  async search(query) {
    return this.get(`/search?q=${encodeURIComponent(query)}`);
  }

  // Health check
  async healthCheck() {
    return this.get('/health');
  }

  // Admin endpoints
  async adminLogin(credentials) {
    const formData = new FormData();
    formData.append('email', credentials.email);
    formData.append('password', credentials.password);
    
    return this.postFormData('/admin/login', formData);
  }

  async getAdminDashboard(cacheBuster = '') {
    return this.get(`/admin/dashboard${cacheBuster}`);
  }

  async getRecentScans(limit = 10) {
    return this.get(`/admin/recent-scans?limit=${limit}`);
  }


  async getAdminUsers(cacheBuster = '') {
    return this.get(`/admin/users${cacheBuster}`);
  }

  async promoteUser(userId, promote = true) {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('promote', promote);
    return this.postFormData('/admin/promote-user', formData);
  }

  async editUser(userId, userData) {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('name', userData.name);
    formData.append('email', userData.email);
    formData.append('role', userData.role);
    formData.append('status', userData.status);
    formData.append('subscription', userData.subscription);
    formData.append('notification_preferences', JSON.stringify(userData.notificationPreferences || {}));
    return this.postFormData('/admin/edit-user', formData);
  }

  // PayPal Configuration methods
  async getPayPalConfig() {
    return this.get('/admin/paypal-config');
  }

  async savePayPalConfig(config) {
    const formData = new FormData();
    formData.append('client_id', config.clientId);
    formData.append('client_secret', config.clientSecret);
    formData.append('environment', config.environment);
    formData.append('webhook_id', config.webhookId);
    formData.append('enabled', config.enabled);
    return this.postFormData('/admin/paypal-config', formData);
  }

  async testPayPalConnection() {
    return this.post('/admin/test-paypal', {});
  }

  // Subscription order methods
  async createSubscriptionOrder(planId, billingCycle, userEmail = null) {
    const formData = new FormData();
    formData.append('plan_id', planId);
    formData.append('billing_cycle', billingCycle);
    if (userEmail) {
      formData.append('user_email', userEmail);
    }
    return this.postFormData('/subscription/create-order', formData);
  }

  async captureSubscriptionOrder(orderId) {
    const formData = new FormData();
    formData.append('order_id', orderId);
    return this.postFormData('/subscription/capture-order', formData);
  }

  async banUser(userId) {
    return this.post(`/admin/users/${userId}/ban`, {});
  }

  async unbanUser(userId) {
    return this.post(`/admin/users/${userId}/unban`, {});
  }

  async deleteUser(userId) {
    const response = await fetch(`${API}/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async refreshItemMaps(itemUid) {
    const response = await fetch(`${API}/api/admin/items/${itemUid}/refresh-maps`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async bulkRefreshAllMaps() {
    const response = await fetch(`${API}/api/admin/bulk-refresh-maps`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async refreshIndividualStoryMap(itemUid, storyId) {
    const response = await fetch(`${API}/api/admin/stories/${itemUid}/${storyId}/refresh-map`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async bulkDeleteUsers(userEmails) {
    return this.post('/admin/users/bulk-delete', userEmails);
  }

  async adminBulkAddStory(storyData) {
    const formData = new FormData();
    formData.append('uids', JSON.stringify(storyData.uids));
    formData.append('story', storyData.story);
    formData.append('location', storyData.location);
    formData.append('coordinates', JSON.stringify(storyData.coordinates));
    formData.append('contributorName', storyData.contributorName);
    formData.append('contributorEmail', storyData.contributorEmail);
    
    if (storyData.storyImage) {
      formData.append('storyImage', storyData.storyImage);
    }

    const response = await fetch(`${API}/api/admin/items/bulk-add-story`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async deleteItemAdmin(uid) {
    const response = await fetch(`${API}/api/admin/items/${uid}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async updateItemAdmin(uid, updatedData) {
    const response = await fetch(`${API}/api/admin/items/${uid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedData),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async moderateStory(storyId, action, reason = '') {
    const formData = new FormData();
    formData.append('action', action);
    if (reason) formData.append('reason', reason);
    
    return this.postFormData(`/admin/stories/${storyId}/moderate`, formData);
  }

  // Subscription endpoints
  async updateSubscription(plan, userId = null) {
    const formData = new FormData();
    formData.append('plan', plan);
    if (userId) formData.append('user_id', userId);
    
    return this.postFormData('/subscription/update', formData);
  }

  async mockPayment(plan) {
    const formData = new FormData();
    formData.append('plan', plan);
    const amount = plan === 'premium' ? 9.99 : plan === 'enterprise' ? 29.99 : 0;
    formData.append('amount', amount);
    
    return this.postFormData('/payment/mock', formData);
  }

  async getSubscriptionFeatures(plan) {
    return this.get(`/subscription/features/${plan}`);
  }

  // Bulk QR code generation
  async createBulkCodes(quantity, prefix = '', userEmail = null, privateTag = '', preRegData = null, photoFile = null, locationData = null) {
    try {
      const formData = new FormData();
      formData.append('quantity', quantity);
      formData.append('prefix', prefix);
      if (userEmail) formData.append('user_email', userEmail);
      if (privateTag) formData.append('private_tag', privateTag);
      // Pre-registration fields (Premium)
      if (preRegData) {
        if (preRegData.ownerName) formData.append('owner_name', preRegData.ownerName);
        if (preRegData.ownerEmail) formData.append('owner_email', preRegData.ownerEmail);
        if (preRegData.itemName) formData.append('item_name', preRegData.itemName);
        if (preRegData.startingStory) formData.append('starting_story', preRegData.startingStory);
      }
      if (photoFile) formData.append('photo', photoFile);
      if (locationData) {
        formData.append('location_name', locationData.name);
        if (locationData.coordinates) {
          formData.append('latitude', locationData.coordinates.lat);
          formData.append('longitude', locationData.coordinates.lng);
        }
      }
      
      console.log('🚀 Starting bulk codes request with:', { quantity, prefix, userEmail, privateTag });
      console.log('🔗 API Base URL:', API);
      console.log('🎯 Full endpoint URL:', `${API}/api/admin/bulk-codes`);
      
      // Enhanced error tracking
      console.log('📡 Making request to backend...');
      const startTime = Date.now();
      
      // Return blob for CSV download - FIXED ENDPOINT PATH
      const response = await fetch(`${API}/api/admin/bulk-codes`, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type for FormData - browser will set it automatically with boundary
        }
      });
      
      const endTime = Date.now();
      console.log(`⏱️ Request completed in ${endTime - startTime}ms`);
      console.log('📊 Response status:', response.status, response.statusText);
      console.log('📋 Response headers:');
      console.log('  - Content-Type:', response.headers.get('content-type'));
      console.log('  - Content-Length:', response.headers.get('content-length'));
      console.log('  - Content-Disposition:', response.headers.get('content-disposition'));
      console.log('  - Cache-Control:', response.headers.get('cache-control'));
      
      if (!response.ok) {
        console.error('❌ API request failed with status:', response.status);
        let errorText = '';
        let errorData = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorText = JSON.stringify(errorData, null, 2);
            console.error('📄 JSON Error response:', errorData);
          } else {
            errorText = await response.text();
            console.error('📄 Text Error response:', errorText);
          }
        } catch (parseError) {
          console.error('❌ Failed to parse error response:', parseError);
          errorText = 'Unable to parse error response';
        }
        
        const detailedError = errorData?.detail || errorData?.message || errorText || 'Unknown error';
        throw new Error(`HTTP ${response.status} ${response.statusText}: ${detailedError}`);
      }
      
      console.log('✅ Response OK, creating blob...');
      const blob = await response.blob();
      console.log('📦 Created blob - Type:', blob.type, 'Size:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        throw new Error('Received empty blob from server');
      }
      
      console.log('🎉 Bulk codes request completed successfully!');
      return blob;
      
    } catch (error) {
      console.error('💥 Bulk codes generation failed:');
      console.error('  Error type:', error.constructor.name);
      console.error('  Error message:', error.message);
      console.error('  Stack trace:', error.stack);
      
      // Re-throw with enhanced context
      if (error.message.includes('Failed to fetch')) {
        throw new Error(`Network error: Unable to reach backend server at ${API}/api/admin/bulk-codes. Check if backend is running and accessible.`);
      } else if (error.message.includes('HTTP 404')) {
        throw new Error(`Endpoint not found: ${API}/api/admin/bulk-codes. This may be a routing issue.`);
      } else if (error.message.includes('HTTP 401')) {
        throw new Error('Authentication required: Please ensure you are logged in as admin.');
      } else if (error.message.includes('HTTP 403')) {
        throw new Error('Access denied: You do not have permission to generate bulk codes.');
      } else {
        throw error; // Re-throw original error if it's already detailed
      }
    }
  }

  async checkUidAvailability(uid) {
    return this.get(`/admin/check-uid/${uid}`);
  }

  async initializeBulkItem(uid, itemData) {
    const formData = new FormData();
    formData.append('itemName', itemData.name);
    formData.append('creatorName', itemData.creatorName);
    formData.append('creatorEmail', itemData.creatorEmail);
    formData.append('initialLocation', itemData.initialLocation);
    formData.append('initialStory', itemData.initialStory);
    
    if (itemData.coordinates) {
      formData.append('latitude', itemData.coordinates.lat);
      formData.append('longitude', itemData.coordinates.lng);
    }
    
    if (itemData.photo) {
      formData.append('photo', itemData.photo);
    }
    
    return this.postFormData(`/items/${uid}/initialize`, formData);
  }

  // Get platform statistics (cache-busting version)
  async getPlatformStats() {
    // Try new bypass endpoint first, then fallback chain
    try {
      return await this.get('/bypass-stats');
    } catch (error) {
      console.warn('Bypass stats failed, trying cache-busting v2:', error);
      try {
        return await this.get('/stats-v2');
      } catch (error2) {
        console.warn('Cache-busting stats v2 failed, trying original:', error2);
        return await this.get('/stats');
      }
    }
  }

  // Get detailed engagement statistics (with caching)
  async getEngagementStats() {
    const cached = this._getCached('engagement');
    if (cached) return cached;
    
    const data = await this.get('/stats/engagement');
    this._setCache('engagement', data, 'engagement');
    return data;
  }

  // Admin revenue analytics methods
  async getRevenueAnalytics() {
    return this.get('/admin/revenue-analytics');
  }

  async getSubscriptionDetails() {
    return this.get('/admin/subscription-details');
  }

  // Analytics methods
  async getAdminAnalytics(timeRange = '7d') {
    return this.get(`/admin/analytics?time_range=${timeRange}`);
  }

  async getMonthlyTrends(months = 12) {
    return this.get(`/admin/analytics/monthly-trends?months=${months}`);
  }

  async getDailyTrends(days = 30) {
    return this.get(`/admin/analytics/daily-trends?days=${days}`);
  }

  async getAdminRecentActivity(limit = 20) {
    return this.get(`/admin/recent-activity?limit=${limit}`);
  }

  async trackAnalyticsEvent(eventType, eventData) {
    return this.post('/analytics/track', {
      event_type: eventType,
      data: eventData
    });
  }

  async getRealtimeAnalytics() {
    return this.get('/analytics/realtime');
  }

  // User Authentication methods
  async registerUser(name, email, password) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('password', password);
    return this.postFormData('/auth/register', formData);
  }

  async userLogin(email, password) {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    return this.postFormData('/auth/login', formData);
  }

  // Magic Link Authentication
  async sendMagicLink(email) {
    const formData = new FormData();
    formData.append('email', email);
    return this.postFormData('/auth/send-magic-link', formData);
  }

  async verifyMagicLink(token) {
    const formData = new FormData();
    formData.append('token', token);
    return this.postFormData('/auth/verify-magic-link', formData);
  }

  // Email verification and password methods
  async checkEmailStatus(email) {
    const formData = new FormData();
    formData.append('email', email);
    return this.postFormData('/auth/check-email', formData);
  }

  async verifyPassword(email, password) {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    return this.postFormData('/auth/verify-password', formData);
  }

  async createItemWithVerification(itemData) {
    const formData = new FormData();
    Object.keys(itemData).forEach(key => {
      if (Array.isArray(itemData[key])) {
        formData.append(key, itemData[key].join(','));
      } else {
        formData.append(key, itemData[key] || '');
      }
    });
    return this.postFormData('/items/create-with-verification', formData);
  }

  async verifyAndCreateItem(token) {
    const formData = new FormData();
    formData.append('token', token);
    return this.postFormData('/items/verify-and-create', formData);
  }

  // Enhanced Authentication Methods
  async forgotPassword(email) {
    const formData = new FormData();
    formData.append('email', email);
    return this.postFormData('/auth/forgot-password', formData);
  }

  async resetPassword(token, newPassword) {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('new_password', newPassword);
    return this.postFormData('/auth/reset-password', formData);
  }

  async changePassword(currentPassword, newPassword, email) {
    const formData = new FormData();
    formData.append('current_password', currentPassword);
    formData.append('new_password', newPassword);
    formData.append('email', email);
    return this.postFormData('/auth/change-password', formData);
  }

  // New Password Setup Methods
  async setupPassword(token, password, name = null) {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('password', password);
    if (name) formData.append('name', name);
    return this.postFormData('/auth/setup-password', formData);
  }

  async sendPasswordSetupEmail(email) {
    const formData = new FormData();
    formData.append('email', email);
    return this.postFormData('/auth/send-password-setup', formData);
  }

  async convertPendingAccount(email, password, name = null) {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    if (name) formData.append('name', name);
    return this.postFormData('/auth/convert-pending-account', formData);
  }

  async checkAccountStatus(email) {
    const formData = new FormData();
    formData.append('email', email);
    return this.postFormData('/auth/check-email-status', formData);
  }

  // Email configuration methods
  async getSmtpSettings() {
    return this.get('/admin/email/smtp-settings');
  }

  async saveSmtpSettings(settings) {
    const formData = new FormData();
    Object.keys(settings).forEach(key => {
      formData.append(key, settings[key]);
    });
    return this.postFormData('/admin/email/smtp-settings', formData);
  }

  async testSmtpSettings(settings) {
    const formData = new FormData();
    Object.keys(settings).forEach(key => {
      formData.append(key, settings[key]);
    });
    return this.postFormData('/admin/email/test-smtp', formData);
  }

  // SendGrid configuration methods
  async getSendGridSettings() {
    return this.get('/admin/email/sendgrid-settings');
  }

  async saveSendGridSettings(settings) {
    const formData = new FormData();
    Object.keys(settings).forEach(key => {
      formData.append(key, settings[key]);
    });
    return this.postFormData('/admin/email/sendgrid-settings', formData);
  }

  async testSendGridSettings(settings) {
    const formData = new FormData();
    Object.keys(settings).forEach(key => {
      formData.append(key, settings[key]);
    });
    return this.postFormData('/admin/email/test-sendgrid', formData);
  }

  // Email template methods
  async getEmailTemplates() {
    return this.get('/admin/email/templates');
  }

  async getEmailTemplate(templateName) {
    return this.get(`/admin/email/templates/${templateName}`);
  }

  async saveEmailTemplate(template) {
    const formData = new FormData();
    formData.append('name', template.name);
    formData.append('subject', template.subject);
    formData.append('html_body', template.html_body);
    formData.append('plain_body', template.plain_body || '');
    formData.append('description', template.description || '');
    formData.append('variables', JSON.stringify(template.variables || []));
    return this.postFormData('/admin/email/templates', formData);
  }

  async sendTestEmail(templateName, testEmail) {
    const formData = new FormData();
    formData.append('template_name', templateName);
    formData.append('test_email', testEmail);
    return this.postFormData('/admin/email/send-test', formData);
  }

  // Product management methods
  async getProducts(clubTag = null) {
    const params = clubTag ? `?clubTag=${encodeURIComponent(clubTag)}` : '';
    return this.get(`/products${params}`);  // Public endpoint - returns only active products
  }

  async getAdminProducts() {
    return this.get('/admin/products');  // Admin endpoint - returns all products
  }

  async createProduct(productData) {
    const formData = new FormData();
    formData.append('name', productData.name);
    formData.append('description', productData.description);
    formData.append('price', productData.price.toString());
    formData.append('category', productData.category);
    formData.append('image', productData.image || '');
    formData.append('inStock', productData.inStock.toString());
    formData.append('featured', productData.featured.toString());
    formData.append('badge', productData.badge || '');
    formData.append('clubTags', (productData.clubTags || []).join(','));
    
    return this.postFormData('/admin/products', formData);
  }

  async updateProduct(productId, productData) {
    const formData = new FormData();
    formData.append('name', productData.name);
    formData.append('description', productData.description);
    formData.append('price', productData.price.toString());
    formData.append('category', productData.category);
    formData.append('image', productData.image || '');
    formData.append('inStock', productData.inStock.toString());
    formData.append('featured', productData.featured.toString());
    formData.append('badge', productData.badge || '');
    formData.append('clubTags', (productData.clubTags || []).join(','));
    
    const response = await fetch(`${API}/api/admin/products/${productId}`, {
      method: 'PUT',
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  }

  async deleteProduct(productId) {
    const response = await fetch(`${API}/api/admin/products/${productId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete product error:', response.status, errorText);
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async reorderProducts(productOrder) {
    const response = await fetch(`${API}/api/admin/products/reorder`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ productOrder })
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async toggleProductActive(productId) {
    const response = await fetch(`${API}/api/admin/products/${productId}/toggle-active`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // Order Management
  async createOrder(orderData) {
    return this.post('/orders', orderData);
  }

  async getOrders(status = null, limit = 50, skip = 0) {
    let url = `/admin/orders?limit=${limit}&skip=${skip}`;
    if (status) url += `&status=${status}`;
    return this.get(url);
  }

  // Admin: manually create an order (phone/email/in-person sales)
  async createManualOrder(orderData) {
    const formData = new FormData();
    formData.append('customer_first_name', orderData.firstName || '');
    formData.append('customer_last_name', orderData.lastName || '');
    formData.append('customer_email', orderData.email || '');
    formData.append('customer_phone', orderData.phone || '');
    formData.append('ship_address', orderData.address || '');
    formData.append('ship_address2', orderData.address2 || '');
    formData.append('ship_city', orderData.city || '');
    formData.append('ship_state', orderData.state || '');
    formData.append('ship_zip', orderData.zip || '');
    formData.append('ship_country', orderData.country || 'USA');
    formData.append('items_json', JSON.stringify(orderData.items || []));
    formData.append('notes', orderData.notes || '');
    formData.append('status', orderData.status || 'pending');
    formData.append('payment_method', orderData.paymentMethod || 'manual');
    return this.postFormData('/admin/orders/manual', formData);
  }

  async updateOrderStatus(orderNumber, status) {
    const formData = new FormData();
    formData.append('status', status);
    return this.patchFormData(`/admin/orders/${orderNumber}/status`, formData);
  }

  async getShippingLabel(orderNumber) {
    const response = await fetch(`${API}/api/admin/orders/${orderNumber}/shipping-label`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Shipping label error:', response.status, errorText);
      throw new Error('Failed to generate shipping label');
    }
    return response.blob();
  }

  // Business Address Configuration
  async getBusinessAddress() {
    return this.get('/admin/business-address');
  }

  async saveBusinessAddress(addressData) {
    const formData = new FormData();
    Object.keys(addressData).forEach(key => {
      formData.append(key, addressData[key]);
    });
    return this.postFormData('/admin/business-address', formData);
  }

  // PayPal Storefront Orders
  async createPayPalOrder(orderData) {
    return this.post('/paypal/create-order', orderData);
  }

  async capturePayPalOrder(orderId) {
    return this.post(`/paypal/capture-order/${orderId}`, {});
  }

  // PayPal Subscriptions
  async createPayPalSubscription(planId, billingCycle, userEmail = null) {
    const formData = new FormData();
    formData.append('plan_id', planId);
    formData.append('billing_cycle', billingCycle);
    if (userEmail) formData.append('user_email', userEmail);
    return this.postFormData('/paypal/create-subscription', formData);
  }

  async capturePayPalSubscription(orderId, userEmail = null) {
    const formData = new FormData();
    if (userEmail) formData.append('user_email', userEmail);
    return this.postFormData(`/paypal/capture-subscription/${orderId}`, formData);
  }

  // Club Creator inquiry submission
  async submitClubInquiry(formData) {
    return this.post('/club-inquiry', formData);
  }

  // Admin: Get all club inquiries
  async getClubInquiries() {
    return this.get('/admin/club-inquiries');
  }

  // Admin: Update club inquiry status
  async updateClubInquiry(inquiryId, data) {
    return this.post(`/admin/club-inquiries/${inquiryId}`, data);
  }

  // Admin: Get all active clubs
  async getActiveClubs() {
    return this.get('/admin/clubs');
  }


  async uploadProductImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return this.postFormData('/admin/upload-product-image', formData);
  }

  // Bulk item creation
  async createBulkItems(bulkData) {
    const formData = new FormData();
    formData.append('itemBaseName', bulkData.itemBaseName);
    formData.append('creatorName', bulkData.creatorName);
    formData.append('creatorEmail', bulkData.creatorEmail);
    formData.append('initialLocation', bulkData.initialLocation);
    formData.append('initialStory', bulkData.initialStory);
    formData.append('categories', bulkData.categories.join(','));
    formData.append('privateTag', bulkData.privateTag || '');
    formData.append('quantity', bulkData.quantity);
    formData.append('coordinates', bulkData.coordinates ? JSON.stringify(bulkData.coordinates) : '');
    
    return this.postFormData('/items/bulk', formData);
  }

  // User subscription methods
  async getUserSubscriptionStatus(userEmail = null) {
    const params = userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : '';
    return this.get(`/user/subscription-status${params}`);
  }

  async cancelUserSubscription(email, paypalSubscriptionId) {
    const formData = new FormData();
    formData.append('user_email', email);
    formData.append('paypal_subscription_id', paypalSubscriptionId);
    return this.postFormData('/user/cancel-subscription', formData);
  }

  // User discount methods

  async calculateStorePrice(originalPrice, userEmail = null) {
    const formData = new FormData();
    formData.append('original_price', originalPrice);
    if (userEmail) {
      formData.append('user_email', userEmail);
    }
    return this.postFormData('/store/calculate-price', formData);
  }

  // Bulk QR registration
  async bulkRegisterOwnership(formData) {
    return this.postFormData('/api/items/bulk-register-ownership', formData);
  }

  // Admin: Batch management
  async listBatches(limit = 100, skip = 0) {
    return this.get(`/admin/batches?limit=${limit}&skip=${skip}`);
  }
  async removeUidFromBatch(batchId, uid) {
    const response = await fetch(`${API}/api/admin/batches/${batchId}/uids/${uid}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
    return response.json();
  }
  async deleteBatchById(batchId) {
    const response = await fetch(`${API}/api/admin/batches/${batchId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
    return response.json();
  }

  // Pre-upload a story image (used by bulk register to avoid re-upload on retries)
  async uploadStoryImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    return this.postFormData('/upload/story-image', formData);
  }

  // User profile management
  async updateUserProfile(profileData) {
    const formData = new FormData();
    formData.append('name', profileData.name);
    formData.append('email', profileData.email);
    formData.append('notificationPreferences', JSON.stringify(profileData.notificationPreferences));
    return this.postFormData('/auth/update-profile', formData);
  }

  // Get user statistics
  async getUserStats(userEmail) {
    return this.get(`/user/stats?user_email=${encodeURIComponent(userEmail)}`);
  }

  // Get user info by email (stub - returns null if not found)
  async getUserInfo(userEmail) {
    try {
      // This is a stub method - user info should come from auth context
      // For now, return basic info from localStorage if available
      const storedUser = localStorage.getItem('trackme_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.email === userEmail) {
          return user;
        }
      }
      return null;
    } catch (error) {
      console.warn('getUserInfo stub method:', error);
      return null;
    }
  }

  // Item registration for bulk QR codes
  async registerItemOwnership(registrationData) {
    return this.postFormData('/api/items/register-ownership', registrationData);
  }

  // Admin email sending
  async adminSendEmail(emailData) {
    const formData = new FormData();
    formData.append('to_email', emailData.to_email);
    formData.append('subject', emailData.subject);
    formData.append('message', emailData.message);
    if (emailData.recipient_name) {
      formData.append('recipient_name', emailData.recipient_name);
    }
    return this.postFormData('/admin/send-email', formData);
  }

  // Admin item ownership management
  async registerItemOwner(uid, ownerData) {
    return this.post(`/admin/items/${uid}/register-owner`, ownerData);
  }

  async bulkRegisterOwners() {
    return this.post('/admin/items/bulk-register-owners', {});
  }

  // Get current user data
  async getCurrentUser() {
    try {
      const response = await this.get('/auth/me');
      return response;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  // AI story enhancement feature has been removed. Stub kept so existing
  // callers do not crash if referenced.
  async enhanceStoryAI() {
    return { success: false, removed: true, error: 'AI story enhancement has been removed.' };
  }

  // Admin AI Configuration — feature removed.
  async getAIConfig() {
    return { success: false, removed: true };
  }
  async saveAIConfig() {
    return { success: false, removed: true };
  }
  async getAIStats() {
    return { success: false, removed: true };
  }

  // Story Reactions
  async reactToStory(itemUid, storyId, emoji, userEmail, userName) {
    const formData = new FormData();
    formData.append('emoji', emoji);
    // Only append email/name if provided (allow anonymous reactions)
    if (userEmail) {
      formData.append('user_email', userEmail);
    }
    if (userName) {
      formData.append('user_name', userName);
    }
    return this.postFormData(`/stories/${itemUid}/${storyId}/react`, formData);
  }

  async getFlaggedStories() {
    return this.get('/admin/flagged-stories');
  }

  async approveFlaggedStory(storyId) {
    return this.post(`/admin/flagged-stories/${storyId}/approve`, {});
  }

  async deleteFlaggedStory(storyId) {
    return this.post(`/admin/flagged-stories/${storyId}/delete`, {});
  }

  // Gamification / Guestbook APIs
  async getUserPoints(userEmail) {
    return this.get(`/gamification/user-points/${encodeURIComponent(userEmail)}`);
  }

  async getLeaderboard(limit = 50) {
    return this.get(`/gamification/leaderboard?limit=${limit}`);
  }

  async createGuestbookLocation(locationData) {
    const formData = new FormData();
    formData.append('name', locationData.name);
    formData.append('description', locationData.description);
    formData.append('category', locationData.category || 'landmark');
    formData.append('latitude', locationData.coordinates.lat);
    formData.append('longitude', locationData.coordinates.lng);
    formData.append('creatorEmail', locationData.creatorEmail);
    formData.append('creatorName', locationData.creatorName);
    
    if (locationData.photo) {
      formData.append('photo', locationData.photo);
    }
    
    return this.postFormData('/locations/create', formData);
  }

  async checkProximity(userLat, userLng, itemUid) {
    return this.get(`/items/${itemUid}/proximity?lat=${userLat}&lng=${userLng}`);
  }

}

export const api = new TrackMeAPI();
export default api;